// Modules
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import browserStorage from 'store';
import jwt from 'jsonwebtoken';
import moment from 'moment';
import Auth0 from 'auth0-js';
import SilentAuthenticationHandler from 'auth0-js/src/web-auth/silent-authentication-handler';
import debounce from 'lodash/debounce';
import assign from 'lodash/assign';
import omit from 'lodash/omit';
import appHistory from 'app-history';

// Actions
import * as authActions from './actions';


// Component
class Auth extends React.Component {
  static propTypes = {
    clientId: PropTypes.string.isRequired,
    domain: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    auth: PropTypes.object.isRequired,
    // Prefer watching store to and using componentWillReceiveProps
    // to know whether token has changed
    onNewToken: PropTypes.func,
    location: PropTypes.object,
    dispatch: PropTypes.func.isRequired
  };

  static defaultProps = {
    clientId: null,
    domain: null
  };

  constructor() {
    super();

    this.checkToken = this.checkToken.bind(this);
    this.authenticate = this.authenticate.bind(this);
    this.renewAuth = this.renewAuth.bind(this);
    this.debouncedRenewAuth = debounce(this.renewAuth, 10000, { leading: true, trailing: false });
  }

  componentWillMount() {
    const { clientId, domain, location } = this.props;

    // Check the token every hour, on visibility change and now on mount
    this.refreshTimer = setInterval(this.checkToken, 60 * 60 * 1000);
    document.addEventListener('visibilitychange', this.checkToken);

    // create lock with redirect to login url
    const lock = new Auth0.WebAuth({
      domain,
      clientID: clientId,
      responseType: 'token id_token',
      scope: 'openid email',
      redirectUri: `${window.location.origin}/login`
    });

    this.lock = lock;

    // Load the token from browser storage if possible
    const auth = browserStorage.get('auth') || {};
    if (auth.token && isValid(auth.token) && auth.profile) {
      return authActions.load(auth.token, auth.profile);
    }

    // check the hash for an auth param
    lock.parseHash((parseError, parsed) => {
      if (parseError) return authActions.logout();
      if (parsed) return this.authenticate(parsed);

      // store url for next after login
      browserStorage.set('next', `${location.pathname}${location.hash}${location.search}`);

      // login redirect
      lock.authorize();
    });
  }

  componentWillReceiveProps(nextProps) {
    const { onNewToken, auth: { token: newToken } } = nextProps;
    const token = this.props.auth.token;
    const hasNewToken = newToken && newToken !== token;

    // When you get a new token let the app know so it can set up interceptors and load users
    if (hasNewToken && onNewToken) onNewToken(newToken, jwt.decode(newToken));

    this.checkToken();
  }

  componentDidUpdate() {
    const { token } = this.props.auth;
    const { lock } = this;
    const { location } = this.props;

    if (!token && lock) {
      browserStorage.set('next', `${location.pathname}${location.hash}${location.search}`);
      lock.authorize();
    }
  }

  componentWillUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  authenticate(result) {
    authActions.load(result.idToken, result.idTokenPayload);
    appHistory.push(browserStorage.get('next'));
  }

  renewAuth() {
    const { clientId, domain } = this.props;

    const options = { scope: 'openid email', redirectUri: `${window.location.origin}/static/silent.html`, prompt: 'none' };
    const blacklist = ['domain', 'plugins', '_send_telemetry', '_sendTelemetry', 'tenant', 'token_issuer', 'root_url', 'rootUrl'];
    const auth0 = new Auth0.WebAuth({ clientID: clientId, domain, responseType: 'token id_token' });
    const params = auth0.transactionManager.process(assign({}, auth0.baseOptions, options));
    const url = auth0.client.buildAuthorizeUrl(omit(params, blacklist));
    const handler = SilentAuthenticationHandler.create({ authenticationUrl: url, postMessageDataType: false });

    handler.login(true, (authError, hash) => {
      if (authError) return authActions.logout();

      auth0.parseHash({ hash, _idTokenVerification: false }, (parseError, parsed) => {
        if (parseError) authActions.logout();

        this.authenticate(parsed);
      });
    });
  }

  checkToken() {
    const { token } = this.props.auth;

    // If we don't have a token we might already be in the login workflow, just bail out
    if (!token) return;

    // If the token is already expired logout
    if (!isValid(token)) authActions.logout();

    // If the token expires in the next two hours go ahead and refresh it
    const twoHoursFromNow = moment().add(2, 'hours');
    const needsRenewal = expires(token).isBefore(twoHoursFromNow);

    if (needsRenewal) this.debouncedRenewAuth();
  }

  render() {
    const { token, profile } = this.props.auth;

    if (!token || !profile) return null;

    return <div>{this.props.children}</div>;
  }
}


// Internal methods
function isValid(token) {
  if (!token) return false;

  return expires(token).isAfter(moment());
}

function expires(token) {
  const payload = jwt.decode(token);
  return moment(payload.exp * 1000);
}

function mapStateToProps(state) {
  return {
    auth: state.auth,
    location: state.location
  };
}


// Exports
export default connect(mapStateToProps)(Auth);



// WEBPACK FOOTER //
// ./app/modules/auth/auth.js